import type { ColDef } from 'ag-grid-community'
import { MISSING_STAT } from '@sportninja/common/features/profile/stats/constants'
import type {
  SeasonGridRow,
  StatColumn,
} from '@sportninja/common/features/profile/stats/types'
import { capitalizeWords } from '@sportninja/common/ts-utils'

import SeasonLabelCell from '../components/cells/SeasonLabelCell'

export const SEASON_GRID_CENTER_CLASS = 'season-grid-center'

/**
 * `cellClass` for the label column. Its renderers are blocks, not plain text,
 * so they don't ride the cell's line-height; the cell centres them instead.
 */
export const SEASON_GRID_LABEL_CELL_CLASS = 'season-grid-label-cell'

const LABEL_COLUMN_WIDTH = 320
const STAT_COLUMN_MIN_WIDTH = 64

/**
 * Season table columns: the pinned team / season label, then one column per
 * stat abbr in the order the view model gives. Rows keep their fixed
 * newest-first order, so nothing sorts.
 */
export const useSeasonGridColumns = (
  columns: StatColumn[]
): ColDef<SeasonGridRow>[] => {
  'use memo'

  return [
    {
      colId: 'label',
      headerName: capitalizeWords('Team / Season'),
      pinned: 'left',
      width: LABEL_COLUMN_WIDTH,
      sortable: false,
      cellRenderer: SeasonLabelCell,
      cellClass: SEASON_GRID_LABEL_CELL_CLASS,
    },
    ...columns.map(
      ({ abbr, name }): ColDef<SeasonGridRow> => ({
        colId: abbr,
        headerName: abbr,
        headerTooltip: name,
        valueGetter: ({ data }) =>
          data === undefined || data.kind === 'loading' || data.kind === 'error'
            ? ''
            : data.values[abbr] ?? MISSING_STAT,
        minWidth: STAT_COLUMN_MIN_WIDTH,
        flex: 1,
        sortable: false,
        headerClass: SEASON_GRID_CENTER_CLASS,
        cellStyle: { textAlign: 'center' },
      })
    ),
  ]
}

export default useSeasonGridColumns
